import { cx } from 'emotion';
import React, {
  CSSProperties,
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import CashierButton from '@ezugi/bootstrap/src/variants/components/evo/EvoBetToolbarButtons/CashierButton';
import { SIZES } from '@ezugi/constants';
import { useClickOutside } from '@ezugi/hooks/src/useClickOutside';
import { Chip } from '@ezugi/primitives';
import type { TableChip } from '@ezugi/types';
import { DeviceInfo, isEvoWrapped } from '@ezugi/utils';

import { useSwipe } from '../../../hooks/src';
import { radialSelectedClicked } from './ChipSelector.css';
import * as styles from './style';
import { ChipPosition, getRadialPositions } from './utils';

const { Container, Backdrop, ChipContainer } = styles;

const getRadialStyles = (open: boolean, positions: ChipPosition[], i: number) => {
  const { right, bottom } = positions[i];
  const transforms = open
    ? { transform: `translate(${-right}px, ${-bottom}px)` }
    : { transform: 'translate(0, 0) scale(1)' };
  return {
    transitionDelay: `0s, ${0.02 * i}s, ${0.02 * i}s`,
    ...transforms,
  };
};

type ChipDisplayValueFn = (value: number) => string;

export type ChipSelectorProps = {
    className?: string;
    onChange?: (chip: TableChip) => void;
    selected?: TableChip;
    vertical?: boolean;
    radial?: boolean;
    size?: number;
    style?: CSSProperties;
    id?: string;
    chips: TableChip[];
    disabled?: boolean;
    chipDisplay?: ChipDisplayValueFn;
    balance: number;
    radiusScale?: number;
    handleToggle?: () => void;
    allowFlyingChips?: boolean;
}

const defaultChipDisplayValue: ChipDisplayValueFn = (value: number) => value.toString();

export const ChipSelector = React.memo(({
  className,
  chips,
  size: chipSize = SIZES.CHIP_SIZE,
  onChange,
  selected: selectedChip,
  style = {},
  vertical = false,
  disabled = false,
  radial = DeviceInfo.isHandheld,
  chipDisplay = defaultChipDisplayValue,
  balance = Infinity,
  handleToggle = () => {},
  radiusScale = 1,
  allowFlyingChips = true,
  ...props
}: ChipSelectorProps) => {
  const [ open, setOpen ] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [ isClicked, setIsClicked ] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useClickOutside(containerRef, close);

  const {
    handlers,
    down: swipeDown,
    left: swipeLeft,
    right: swipeRight,
  } = useSwipe();

  useEffect(() => {
    if (swipeDown || swipeRight || swipeLeft) {
      setOpen(false);
    }
  }, [ swipeDown, swipeRight, swipeLeft ]);

  useEffect(() => {
    if (disabled) {
      close();
    }
  }, [ disabled, close ]);

  const selectChip = useCallback(
    (chip: TableChip) => {
      close();

      if (onChange) {
        onChange(chip);
      }
    },
    [ onChange, close ]
  );

  const handleClickAnimation = () => {
    setIsClicked(true);
    setTimeout(() => setIsClicked(false), 200);
  };

  const handleRadialToggle = useCallback(
    () => {
      handleToggle();

      if (disabled) {
        setOpen(false);
      } else {
        handleClickAnimation();
        setOpen(!open);
      }
    },
    [ setOpen, disabled, open, handleToggle ],
  );

  const backdropSize = chipSize * 2 * chips.length;

  const totalChips = isEvoWrapped() ? chips.length + 1 : chips.length;
  const positions = useMemo(
    () => getRadialPositions(totalChips, chipSize, vertical, radiusScale),
    [ totalChips, chipSize, vertical, radiusScale ]
  );
  const prevSelectedIndex = useMemo(
    () => {
      const value = chips.findIndex((chip: TableChip) => chip.value === selectedChip?.value) - 1;
      return value < 0 ? chips.length - 1 : value;
    },
    [ chips, selectedChip ]
  );
  const nextSelectedIndex = useMemo(
    () => {
      const value = chips.findIndex((chip: TableChip) => chip.value === selectedChip?.value) + 1;
      return value < 1 || value === chips.length ? 0 : value;
    },
    [ chips, selectedChip ]
  );

  const handleSelectChip = useCallback(
    (chip: TableChip) => {
      if (chip.value > balance) {
        return;
      }

      selectChip(chip);
    },
    [ balance, selectChip ],
  );

  return (
    <Container
      ref={containerRef}
      style={style}
      data-e2e="chip-selector"
      data-testid="chip-selector"
      className={`${className} ${open ? 'chip-selector-opened' : ''}`}
      {...props}
      {...handlers}
    >
      {!!radial && (
        <Backdrop
          data-e2e="chip-selector__backdrop"
          onClick={() => open && setOpen(false)}
          style={{
            width: open ? backdropSize : 0,
            height: open ? backdropSize : 0,
          }}
        />
      )}
      {chips.map((chip, chipIndex: number) => (
        <ChipContainer
          key={chip.value}
          className={cx(
            allowFlyingChips && 'flying-chips',
              `chip-selector__chip-container${
                selectedChip?.value === chip.value ? '--selected' : ''
              }`,
              radial && styles.radialChipContainer,
              selectedChip?.value === chip.value && styles.selectedChip,
              selectedChip?.value === chip.value && !radial && isEvoWrapped() && styles.selectedEvoChip,
              isEvoWrapped() && chipIndex === nextSelectedIndex && !open && radial && styles.nextToSelectedEvoChip,
              isEvoWrapped() && chipIndex === prevSelectedIndex && !open && radial && styles.prevToSelectedEvoChip,
          )}
          style={radial ? getRadialStyles(open, positions, chipIndex) : {}}
          disabled={chip.value > balance}
          onClick={() => handleSelectChip(chip)}
          data-e2e={`chip-selector__chip-container${
              selectedChip?.value === chip.value ? '--selected' : ''
            }`}
        >
          <Chip
            size={chipSize}
            disabled={chip.value > balance}
            {...chip}
            displayValue={chipDisplay(chip.value)}
          />
        </ChipContainer>
      ))}
      {radial && isEvoWrapped() && (
        <ChipContainer
          key="cashier-btn"
          className={cx(styles.radialChipContainer, styles.cashierBtn, {
            open,
          })}
          style={radial ? getRadialStyles(open, positions, chips.length) : {}}
          data-e2e="chip-selector__chip-container--cashier-btn"
        >
          <CashierButton />
        </ChipContainer>
      )}
      {radial && selectedChip && (
        <ChipContainer
          data-testid="selected-radial-chip"
          className={cx(
            styles.selectedRadialChip,
            'selected-radial-chip',
            isEvoWrapped() && styles.selectedEvoChip,
            open && 'open',
            {
              [radialSelectedClicked]: isClicked,
            }
          )}
          onClick={handleRadialToggle}
        >
          <Chip
            size={chipSize}
            {...selectedChip}
            displayValue={chipDisplay(selectedChip.value)}
          />
        </ChipContainer>
      )}
    </Container>
  );
});
